/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * i18n + theme preferences. Both are client-side prefs persisted to
 * localStorage, exactly as in MoveHelper — theme drives the [data-theme]
 * attribute the Sentinelle tokens key off, and `lang` drives <html lang>.
 *
 * Kept as ONE context rather than two hooks because that is how MoveHelper
 * does it, and a person using both apps should find the same control in the
 * same place.
 */

import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from "react";

import { STRINGS, type Lang } from "./strings.ts";

export type Theme = "light" | "dark";

interface PrefsCtx {
  lang: Lang;
  setLang: (l: Lang) => void;
  theme: Theme;
  setTheme: (t: Theme) => void;
  toggleTheme: () => void;
  t: (key: string) => string;
}

const Ctx = createContext<PrefsCtx | null>(null);

function initialLang(): Lang {
  try {
    const saved = localStorage.getItem("mp-lang");
    if (saved === "en" || saved === "fr") return saved;
  } catch { /* private mode */ }
  return navigator.language.startsWith("fr") ? "fr" : "en";
}

function initialTheme(): Theme {
  try {
    const saved = localStorage.getItem("mp-theme");
    if (saved === "light" || saved === "dark") return saved;
  } catch { /* private mode */ }
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function PrefsProvider({ children }: { children: ReactNode }) {
  const [lang, setLang] = useState<Lang>(initialLang);
  const [theme, setTheme] = useState<Theme>(initialTheme);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    try { localStorage.setItem("mp-theme", theme); } catch { /* private mode */ }
  }, [theme]);

  useEffect(() => {
    document.documentElement.setAttribute("lang", lang);
    try { localStorage.setItem("mp-lang", lang); } catch { /* private mode */ }
  }, [lang]);

  const t = useCallback((key: string) => {
    const entry = STRINGS[key];
    // Fall back to EN, then to the key itself — a missing translation should
    // show English, never a blank.
    if (!entry) return key;
    return entry[lang] ?? entry.en;
  }, [lang]);

  const value = useMemo<PrefsCtx>(() => ({
    lang, setLang, theme, setTheme,
    toggleTheme: () => setTheme((x) => (x === "light" ? "dark" : "light")),
    t,
  }), [lang, theme, t]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function usePrefs(): PrefsCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("usePrefs must be used within PrefsProvider");
  return ctx;
}

/** BCP-47 tag for Intl formatters. */
export function localeFor(lang: Lang): string {
  return lang === "fr" ? "fr-FR" : "en-US";
}
